import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Request interceptor - attach access token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor - handle token refresh with retry limit
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    
    // Prevent infinite retry loops
    if (original._retryCount && original._retryCount >= 1) {
      if (!isRefreshing) {
        sessionStorage.removeItem('accessToken');
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
      return Promise.reject(error);
    }
    // Handle Maintenance Mode
    if (error.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE_MODE') {
      window.dispatchEvent(new CustomEvent('app:maintenance'));
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !original._retry) {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          original.headers['Authorization'] = 'Bearer ' + token;
          return api(original);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      original._retry = true;
      original._retryCount = (original._retryCount || 0) + 1;
      isRefreshing = true;

      try {
        // Use a separate axios instance to avoid interceptor loop and send cookies
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });

        if (data.success && data.data) {
          sessionStorage.setItem('accessToken', data.data.accessToken);
          original.headers['Authorization'] = `Bearer ${data.data.accessToken}`;
          processQueue(null, data.data.accessToken);
          return api(original);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        sessionStorage.removeItem('accessToken');
        window.dispatchEvent(new CustomEvent('auth:expired'));
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout', {}, { withCredentials: true }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  refresh: () => api.post('/auth/refresh', {}, { withCredentials: true }),
  getMe: () => api.get('/auth/me'),
};

// User
export const userAPI = {
  getDashboard: () => api.get('/user/dashboard'),
  updateProfile: (data) => api.patch('/user/profile', data),
  uploadAvatar: (formData) => api.post('/user/upload-avatar', formData),
  uploadAvatarCloudinary: (formData) => api.post('/upload/profile', formData),
  uploadGeneral: (formData, folder = 'general') => api.post(`/upload/general?folder=${folder}`, formData),
  deleteAvatar: () => api.delete('/user/delete-avatar'),
  changePassword: (data) => api.post('/user/change-password', data),
  getNotifications: (params) => api.get('/user/notifications', { params }),
  getUnreadCount: () => api.get('/user/notifications/unread-count'),
  markNotificationsRead: (notificationIds) => api.post('/user/notifications/mark-read', { notificationIds }),
  getReferrals: () => api.get('/user/referrals'),
};

// Signals
export const signalsAPI = {
  getSignals: (params) => api.get('/signals', { params }),
  getPublicSignals: () => api.get('/signals/public'),
  getPublicRecentResults: () => api.get('/signals/public/recent'),
  getStats: () => api.get('/signals/stats'),
  getLeaderboard: () => api.get('/signals/leaderboard'),
  getPerformance: () => api.get('/signals/performance'),
  createSignal: (data) => api.post('/signals', data),
  updateSignal: (id, data) => api.put(`/signals/${id}`, data),
  deleteSignal: (id) => api.delete(`/signals/${id}`),
  getAllSignals: (params) => api.get('/signals/admin/all', { params }),
};

// Plans & Payments
export const plansAPI = {
  getPlans: () => api.get('/plans'),
};

export const paymentsAPI = {
  createCheckoutSession: (planId) => api.post('/payments/create-checkout-session', { planId }),
  cancelSubscription: () => api.post('/payments/cancel-subscription'),
  getHistory: (params) => api.get('/payments/history', { params }),
  createCryptoPayment: (data) => api.post('/payments/crypto', data),
  createGCashPayment: (planId) => api.post('/payments/gcash', { planId }),
  createMayaPayment: (planId) => api.post('/payments/maya', { planId }),
};

// Market API
export const marketAPI = {
  getNews: () => api.get('/market/news'),
  getInsights: () => api.get('/market/insights'),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  toggleUserActive: (id) => api.patch(`/admin/users/${id}/toggle-active`),
  updateUserRole: (id, role) => api.patch(`/admin/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  assignPlan: (id, planId) => api.post(`/admin/users/${id}/assign-plan`, { planId }),
  getRevenue: () => api.get('/admin/revenue'),
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  updateSubscription: (id, data) => api.patch(`/admin/subscriptions/${id}`, data),
  updateBotConfig: (data) => api.patch('/admin/bot-config', data),
  getHealth: () => api.get('/health'),
  // Signal management (admin-only)
  createSignal: (data) => api.post('/signals', data),
  updateSignal: (id, data) => api.put(`/signals/${id}`, data),
  deleteSignal: (id) => api.delete(`/signals/${id}`),
  getAllSignals: (params) => api.get('/signals/admin/all', { params }),
  getManualSignalQuotas: () => api.get('/signals/admin/quotas'),
  createAgent: (data) => api.post('/admin/agents', data),
  getDailyStats: () => api.get('/stats/today'),
  getTickets: (params) => api.get('/tickets', { params }),
  updateTicket: (id, data) => api.patch(`/tickets/${id}`, data),
  getTicketMessages: (id) => api.get(`/tickets/${id}/messages`),
  replyToTicket: (id, data) => api.post(`/tickets/${id}/reply`, data),
  createTicket: (data) => api.post('/tickets', data),
  getLandingContent: () => api.get('/public/landing-content'),
  updateLandingContent: (data) => api.post('/admin/landing-content', data),
  bulkUpdatePlans: (userIds, planSlug) => api.post('/admin/users/bulk-assign-plan', { userIds, planSlug }),
  sendAnnouncement: (data) => api.post('/admin/announcement', data),
  searchMT5Servers: (query) => api.get(`/admin/mt5-servers?q=${query}`),
  getAIHealth: () => api.get('/admin/ai-health'),
};

// Bot Status
export const botAPI = {
  getStatus: () => api.get('/admin/bot-status'),
  getSmcStatus: () => api.get('/admin/smc-bot/status'),
  startSmcBot: () => api.post('/admin/smc-bot/start'),
  stopSmcBot: () => api.post('/admin/smc-bot/stop'),
  getBotLogs: () => api.get('/admin/bot/logs'),
  getBotRejectionStats: () => api.get('/admin/bot/rejection-stats'),
  getBotMarketStatus: () => api.get('/admin/bot/market-status'),
  getBotIntelligence: () => api.get('/admin/bot/intelligence'),
  scoutMarket: () => api.get('/admin/bot/scout'),
  getSmcSettings: () => api.get('/admin/smc-bot/settings'),
  updateSmcSettings: (settings) => api.post('/admin/smc-bot/settings', settings),
};

export const publicAPI = {
  getLandingContent: () => api.get('/public/landing-content'),
};

// Public Stats
export const statsAPI = {
  getUserCount: () => api.get('/stats/user-count'),
};

// Chat
export const chatAPI = {
  upload: (formData) => api.post('/chat/upload', formData),
  uploadImage: (id, formData) => api.post(`/conversations/${id}/upload`, formData),
  getConversations: () => api.get('/conversations'),
  createConversation: (data) => api.post('/conversations', data),
  deleteConversation: (id) => api.delete(`/conversations/${id}`),
  getMessages: (id) => api.get(`/conversations/${id}/messages`),
  createMessage: (id, data) => api.post(`/conversations/${id}/messages`, data),
  escalateConversation: (id) => api.post(`/conversations/${id}/escalate`),
};

// AI
export const aiAPI = {
  chat: (message) => api.post('/ai/chat', { message }),
};

// Testimonials API
export const testimonialsAPI = {
  getApproved: () => api.get('/testimonials'),
  getMyTestimonials: () => api.get('/testimonials/my'),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
  // Admin routes
  getAll: (params) => api.get('/admin/testimonials', { params }),
  approve: (id) => api.patch(`/admin/testimonials/${id}/approve`),
  reject: (id) => api.patch(`/admin/testimonials/${id}/reject`),
  toggleHide: (id) => api.patch(`/admin/testimonials/${id}/toggle-hide`),
  updateSortOrder: (id, sortOrder) => api.patch(`/admin/testimonials/${id}/sort-order`, { sortOrder }),
  adminDelete: (id) => api.delete(`/admin/testimonials/${id}`),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
  // Admin only
  getAll: (params) => api.get('/admin/feedback', { params }),
  markAsRead: (id) => api.patch(`/admin/feedback/${id}/read`),
  delete: (id) => api.delete(`/admin/feedback/${id}`),
};

// Broker API (VIP Only)
export const brokerAPI = {
  getConnection: () => api.get('/broker/connection'),
  upsertConnection: (data) => api.post('/broker/connection', data),
  deleteConnection: () => api.delete('/broker/connection'),
  toggleStatus: (status) => api.patch('/broker/status', { status }),
  getExecutions: () => api.get('/broker/executions'),
  getBalance: () => api.get('/broker/balance'),
  testConnection: (data) => api.post('/broker/test', data),
};

export default api;
